import React, { useState, useEffect } from 'react';
import { RefreshCw, Link2, X, Cpu, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { ImageSlot } from './ImageSlot';
import {
  API, fmt, CHUNK_SIZE, MARKUP, DESIGNERS, CATEGORIES, GENDERS,
  CURRENCIES, CAT_SIZES, EMPTY_FORM,
} from './constants';

function toZAR(amount, currency, rates) {
  if (!amount) return 0;
  const n = parseFloat(amount);
  if (currency === 'ZAR') return n;
  return Math.round(n * (rates[currency] || 1));
}

async function uploadImage(file, setProgress) {
  const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
  const uploadId = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  let lastResult = null;
  for (let i = 0; i < totalChunks; i++) {
    const start = i * CHUNK_SIZE;
    const chunk = file.slice(start, start + CHUNK_SIZE);
    const fd = new FormData();
    fd.append('chunk', chunk, file.name);
    fd.append('upload_id', uploadId);
    fd.append('chunk_index', i);
    fd.append('total_chunks', totalChunks);
    fd.append('filename', file.name);
    const { data } = await axios.post(`${API}/admin/upload-image/chunk`, fd, {
      withCredentials: true,
      onUploadProgress: (e) => {
        const pct = Math.round(((i + e.loaded / e.total) / totalChunks) * 100);
        setProgress(pct);
      },
    });
    lastResult = data;
  }
  if (!lastResult?.path) throw new Error('Upload failed');
  return `${process.env.REACT_APP_BACKEND_URL}${lastResult.path}`;
}

export function ProductModal({ editingProduct, rates, onClose, onSaved, syncCatalog }) {
  const isEditing = !!editingProduct;
  const [form, setForm] = useState(EMPTY_FORM);
  const [useAvailable, setUseAvailable] = useState(false);

  // URL import
  const [importUrl, setImportUrl] = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [importMsg, setImportMsg] = useState('');

  // AI scan
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanMsg, setScanMsg] = useState('');

  // Save
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');

  useEffect(() => {
    if (editingProduct) {
      setForm({
        name: editingProduct.name || '',
        designer: editingProduct.designer || '',
        category: editingProduct.category || 'Bags',
        gender: editingProduct.gender || 'Women',
        color: editingProduct.color || '',
        original_price: editingProduct.original_price || '',
        discounted_price: editingProduct.discounted_price || '',
        input_currency: 'ZAR',
        sizes: editingProduct.sizes || [],
        available_sizes: editingProduct.available_sizes || null,
        images: [editingProduct.images?.[0] || '', editingProduct.images?.[1] || '', editingProduct.images?.[2] || ''],
        description: editingProduct.description || '',
        source_website: editingProduct.source_website || '',
        source_url: editingProduct.source_url || '',
        variant_group: editingProduct.variant_group || '',
        scan_timestamp: editingProduct.scan_timestamp || null,
        screenshot_ref: editingProduct.screenshot_ref || '',
        source_status: editingProduct.source_status || 'active',
        is_active: editingProduct.is_active !== false,
      });
      setUseAvailable(Array.isArray(editingProduct.available_sizes));
    } else {
      setForm(EMPTY_FORM);
      setUseAvailable(false);
    }
    setScanResult(null);
    setScanMsg('');
    setImportUrl('');
    setImportMsg('');
    setSaveMsg('');
  }, [editingProduct]);

  const setField = (field, val) => setForm(f => ({ ...f, [field]: val }));

  const origZAR = toZAR(form.original_price, form.input_currency, rates);
  const discZAR = toZAR(form.discounted_price, form.input_currency, rates);
  const finalPrice = Math.round(discZAR * MARKUP);
  const savings = Math.round(origZAR - finalPrice);
  const discPct = origZAR > 0 ? Math.round((savings / origZAR) * 1000) / 10 : 0;
  const catSizes = CAT_SIZES[form.category] || [];

  const handleImport = async () => {
    if (!importUrl.trim()) return;
    setImportLoading(true);
    setImportMsg('');
    try {
      const { data } = await axios.post(`${API}/admin/products/import-url`, { url: importUrl }, { withCredentials: true });
      setForm(f => ({
        ...f,
        name: data.name || f.name,
        description: data.description || f.description,
        images: [data.images?.[0] || f.images[0], f.images[1], f.images[2]],
        original_price: data.original_price || f.original_price,
        discounted_price: data.discounted_price || f.discounted_price,
        source_website: data.source_website || f.source_website,
        source_url: data.source_url || f.source_url,
        input_currency: 'ZAR',
      }));
      const detail = data.raw_price ? ` · ${data.currency} ${data.raw_price}` : '';
      setImportMsg(`Imported from ${data.source_website || 'URL'}${detail}`);
    } catch {
      setImportMsg('Could not auto-import — fill in the details manually.');
    }
    setImportLoading(false);
  };

  const handleScan = async () => {
    const url = form.images.find(Boolean);
    if (!url) { setScanMsg('Add an image first, then scan it.'); return; }
    setScanning(true);
    setScanResult(null);
    setScanMsg('');
    try {
      const { data } = await axios.post(`${API}/admin/analyze-screenshot`, { image_url: url }, { withCredentials: true });
      setScanResult(data);
      if (data.available_sizes?.length || data.all_sizes?.length) {
        const allSizes = data.all_sizes?.length ? data.all_sizes : [...(data.available_sizes || []), ...(data.unavailable_sizes || [])];
        const availSizes = data.available_sizes || [];
        setField('sizes', allSizes);
        if (availSizes.length > 0) { setField('available_sizes', availSizes); setUseAvailable(true); }
      }
      if (data.source_url) setField('source_url', data.source_url);
      if (data.source_website) setField('source_website', data.source_website);
      if (data.detected_brand && !form.designer) setField('designer', data.detected_brand);
      if (data.detected_product_name && !form.name) setField('name', data.detected_product_name);
      setField('scan_timestamp', data.scan_timestamp);
      setField('screenshot_ref', url);
      setScanMsg(`Detected ${data.available_sizes?.length || 0} available, ${data.unavailable_sizes?.length || 0} sold-out sizes`);
    } catch (e) {
      setScanMsg(e.response?.data?.detail || 'Scan failed — try a clearer screenshot showing the size grid');
    }
    setScanning(false);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.designer.trim() || !form.original_price || !form.discounted_price) {
      setSaveMsg('Name, Designer, and both Prices are required.');
      return;
    }
    setSaveLoading(true);
    setSaveMsg('');
    try {
      const payload = {
        name: form.name, designer: form.designer, category: form.category,
        gender: form.gender, color: form.color,
        original_price: toZAR(form.original_price, form.input_currency, rates),
        discounted_price: toZAR(form.discounted_price, form.input_currency, rates),
        sizes: form.sizes,
        available_sizes: useAvailable ? (form.available_sizes || []) : null,
        images: form.images.filter(Boolean),
        description: form.description,
        source_website: form.source_website,
        source_url: form.source_url,
        variant_group: form.variant_group || '',
        is_active: form.is_active,
        scan_timestamp: form.scan_timestamp || null,
        screenshot_ref: form.screenshot_ref || null,
        source_status: form.source_status || 'active',
      };
      if (isEditing) {
        await axios.put(`${API}/admin/products/${editingProduct.product_id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/admin/products`, payload, { withCredentials: true });
      }
      onSaved();
      syncCatalog();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message;
      setSaveMsg(detail ? `Save failed: ${detail}` : 'Save failed. Please try again.');
    }
    setSaveLoading(false);
  };

  const toggleSize = (size, field) => {
    const list = form[field] || [];
    setField(field, list.includes(size) ? list.filter(s => s !== size) : [...list, size]);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full my-6" onClick={e => e.stopPropagation()} data-testid="product-modal">

        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-[#E5E5E5] px-6 py-5 flex items-center justify-between z-10">
          <h2 className="font-playfair text-xl text-[#0A0A0A]">{isEditing ? 'Edit Product' : 'Add Product'}</h2>
          <button onClick={onClose}><X size={20} className="text-[#AAAAAA] hover:text-[#0A0A0A]" /></button>
        </div>

        <div className="p-6 flex flex-col gap-6">

          {/* URL Import */}
          <div className="bg-[#F9F9F9] p-4 border border-[#E5E5E5]">
            <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#D4AF37] font-work mb-3">Import from URL</p>
            <div className="flex gap-2">
              <input
                value={importUrl} onChange={e => setImportUrl(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleImport()}
                placeholder="Paste product URL (farfetch.com, ssense.com, mytheresa.com...)"
                className="flex-1 border border-[#E5E5E5] bg-white px-3 py-2 text-sm font-work focus:outline-none focus:border-[#D4AF37]"
                data-testid="import-url-input"
              />
              <button onClick={handleImport} disabled={importLoading || !importUrl.trim()}
                className="bg-[#0A0A0A] text-white px-4 py-2 text-xs uppercase tracking-wider font-work flex items-center gap-2 disabled:opacity-40 hover:bg-[#D4AF37] hover:text-[#0A0A0A] transition-colors"
                data-testid="import-url-btn">
                {importLoading ? <RefreshCw size={12} className="animate-spin" /> : <Link2 size={12} />}
                Import
              </button>
            </div>
            {importMsg && (
              <p className={`text-xs font-work mt-2 ${importMsg.includes('Could not') ? 'text-red-500' : 'text-[#2E7D32]'}`}>{importMsg}</p>
            )}
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#999999] font-work font-semibold">Product Name *</label>
              <input value={form.name} onChange={e => setField('name', e.target.value)}
                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm font-work mt-1.5 focus:outline-none focus:border-[#D4AF37]"
                data-testid="product-name-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#999999] font-work font-semibold">Designer *</label>
              <input value={form.designer} onChange={e => setField('designer', e.target.value)}
                list="hl-designers" placeholder="e.g. Gucci"
                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm font-work mt-1.5 focus:outline-none focus:border-[#D4AF37]"
                data-testid="product-designer-input" />
              <datalist id="hl-designers">{DESIGNERS.map(d => <option key={d} value={d} />)}</datalist>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#999999] font-work font-semibold">Color</label>
              <input value={form.color} onChange={e => setField('color', e.target.value)}
                placeholder="e.g. Black"
                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm font-work mt-1.5 focus:outline-none focus:border-[#D4AF37]" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#999999] font-work font-semibold">Category *</label>
              <select value={form.category}
                onChange={e => setForm(f => ({ ...f, category: e.target.value, sizes: [], available_sizes: null }))}
                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm font-work mt-1.5 focus:outline-none focus:border-[#D4AF37]"
                data-testid="product-category-select">
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#999999] font-work font-semibold">Gender</label>
              <select value={form.gender} onChange={e => setField('gender', e.target.value)}
                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm font-work mt-1.5 focus:outline-none focus:border-[#D4AF37]">
                {GENDERS.map(g => <option key={g}>{g}</option>)}
              </select>
            </div>
          </div>

          {/* Pricing */}
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold font-work mb-3">Pricing</p>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-[#999999] font-work font-semibold">Currency</label>
                <select value={form.input_currency} onChange={e => setField('input_currency', e.target.value)}
                  className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm font-work mt-1.5 focus:outline-none focus:border-[#D4AF37]">
                  {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-[#999999] font-work font-semibold">Original Price *</label>
                <input type="number" value={form.original_price} onChange={e => setField('original_price', e.target.value)}
                  placeholder="0"
                  className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm font-work mt-1.5 focus:outline-none focus:border-[#D4AF37]"
                  data-testid="product-original-price-input" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-[#999999] font-work font-semibold">Discounted Price *</label>
                <input type="number" value={form.discounted_price} onChange={e => setField('discounted_price', e.target.value)}
                  placeholder="0"
                  className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm font-work mt-1.5 focus:outline-none focus:border-[#D4AF37]"
                  data-testid="product-discounted-price-input" />
              </div>
            </div>
            {origZAR > 0 && discZAR > 0 && (
              <div className="mt-3" data-testid="price-preview">
                {savings < 0 && (
                  <p className="text-xs text-red-500 font-work mb-2">
                    Warning: HEIR LUXX price ({fmt(finalPrice)}) exceeds original ({fmt(origZAR)}). Increase the original price or lower the discounted price.
                  </p>
                )}
                <div className="bg-[#F9F9F9] border border-[#E5E5E5] p-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs font-work">
                  <div><p className="text-[#AAAAAA] mb-0.5">Original</p><p className="font-semibold text-[#0A0A0A]">{fmt(origZAR)}</p></div>
                  <div><p className="text-[#AAAAAA] mb-0.5">Discounted</p><p className="font-semibold text-[#0A0A0A]">{fmt(discZAR)}</p></div>
                  <div><p className="text-[#AAAAAA] mb-0.5">HEIR LUXX Price</p><p className="font-bold text-[#D4AF37]">{fmt(finalPrice)}</p></div>
                  <div>
                    <p className="text-[#AAAAAA] mb-0.5">You Save</p>
                    <p className={`font-semibold ${savings >= 0 ? 'text-[#2E7D32]' : 'text-red-500'}`}>
                      {savings >= 0 ? `${fmt(savings)} (${discPct}%)` : 'No saving'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sizes */}
          {catSizes.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold font-work mb-3">Sizes</p>
              <div className="mb-3">
                <p className="text-[10px] text-[#999999] font-work mb-2">All sizes this product comes in:</p>
                <div className="flex flex-wrap gap-1.5">
                  {catSizes.map(s => (
                    <button key={s} type="button" onClick={() => toggleSize(s, 'sizes')}
                      className={`px-2.5 py-1.5 text-xs font-work border transition-colors ${form.sizes.includes(s) ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]' : 'border-[#E5E5E5] text-[#555555] hover:border-[#D4AF37]'}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              {form.sizes.length > 0 && (
                <div className="border-t border-[#F3F3F3] pt-3">
                  <div className="flex items-center gap-3 mb-2">
                    <button type="button"
                      onClick={() => { setUseAvailable(!useAvailable); if (useAvailable) setField('available_sizes', null); }}
                      className={`relative w-10 h-5 rounded-full transition-colors flex items-center flex-shrink-0 ${useAvailable ? 'bg-[#D4AF37]' : 'bg-[#E5E5E5]'}`}
                      data-testid="use-available-toggle">
                      <span className={`absolute w-3.5 h-3.5 rounded-full bg-white shadow transition-transform ${useAvailable ? 'translate-x-5' : 'translate-x-1'}`} />
                    </button>
                    <span className="text-[10px] text-[#999999] font-work">Specify which sizes are currently in stock (others shown as "sold out")</span>
                  </div>
                  {useAvailable && (
                    <div>
                      <p className="text-[10px] text-[#999999] font-work mb-2">In stock — select all that apply:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {form.sizes.map(s => (
                          <button key={s} type="button" onClick={() => toggleSize(s, 'available_sizes')}
                            className={`px-2.5 py-1.5 text-xs font-work border transition-colors ${(form.available_sizes || []).includes(s) ? 'bg-[#2E7D32] text-white border-[#2E7D32]' : 'border-[#E5E5E5] text-[#555555] hover:border-[#2E7D32]'}`}
                            data-testid={`avail-size-${s}`}>
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Images */}
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold font-work mb-3">Product Images</p>
            <div className="flex flex-col gap-4">
              {form.images.map((img, idx) => (
                <ImageSlot key={`img-slot-${idx}`} index={idx} value={img}
                  onChange={val => {
                    const imgs = [...form.images];
                    imgs[idx] = val;
                    setField('images', imgs);
                  }}
                  onUpload={uploadImage}
                />
              ))}
            </div>
          </div>

          {/* AI Screenshot Scan */}
          <div className="bg-[#0A0A0A] p-4 border border-[#0A0A0A]">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Cpu size={14} className="text-[#D4AF37]" />
                <p className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#D4AF37] font-work">AI Screenshot Scan</p>
              </div>
              {form.scan_timestamp && (
                <span className="text-[9px] text-[#666666] font-work">
                  Last scan: {new Date(form.scan_timestamp).toLocaleString()}
                </span>
              )}
            </div>
            <p className="text-[10px] text-[#AAAAAA] font-work mb-3">
              Upload a screenshot of the product page, then scan it to auto-detect available sizes and source URL.
            </p>
            <button type="button" onClick={handleScan}
              disabled={scanning || !form.images.find(Boolean)}
              className="flex items-center gap-2 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#C49B2E] transition-colors px-4 py-2.5 text-xs uppercase tracking-widest font-semibold font-work disabled:opacity-40"
              data-testid="scan-screenshot-btn">
              {scanning ? <><RefreshCw size={12} className="animate-spin" /> Scanning...</> : <><Cpu size={12} /> Scan Screenshot</>}
            </button>
            {scanMsg && (
              <p className={`text-xs font-work mt-2 flex items-center gap-1.5 ${scanMsg.includes('Detected') ? 'text-[#2E7D32]' : 'text-[#AAAAAA]'}`}
                data-testid="scan-msg">
                {scanMsg.includes('Detected') && <CheckCircle2 size={12} />} {scanMsg}
              </p>
            )}
            {scanResult && (
              <div className="mt-3 grid grid-cols-2 gap-3 text-[10px] font-work">
                {scanResult.available_sizes?.length > 0 && (
                  <div>
                    <p className="text-[#D4AF37] mb-1 uppercase tracking-wider">Available ({scanResult.available_sizes.length})</p>
                    <div className="flex flex-wrap gap-1">
                      {scanResult.available_sizes.map(s => <span key={s} className="bg-[#D4AF37]/20 text-[#D4AF37] px-1.5 py-0.5">{s}</span>)}
                    </div>
                  </div>
                )}
                {scanResult.unavailable_sizes?.length > 0 && (
                  <div>
                    <p className="text-[#555555] mb-1 uppercase tracking-wider">Sold Out ({scanResult.unavailable_sizes.length})</p>
                    <div className="flex flex-wrap gap-1">
                      {scanResult.unavailable_sizes.map(s => <span key={s} className="bg-[#333333] text-[#666666] px-1.5 py-0.5 line-through">{s}</span>)}
                    </div>
                  </div>
                )}
                {scanResult.source_website && (
                  <div className="col-span-2">
                    <p className="text-[#555555] mb-0.5 uppercase tracking-wider">Source Detected</p>
                    <p className="text-white">{scanResult.source_website}{scanResult.source_url ? ` — ${scanResult.source_url.slice(0, 60)}...` : ''}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#999999] font-work font-semibold">Description</label>
            <textarea value={form.description} onChange={e => setField('description', e.target.value)}
              rows={3} placeholder="Product description..."
              className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm font-work mt-1.5 focus:outline-none focus:border-[#D4AF37] resize-none" />
          </div>

          {/* Source */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#999999] font-work font-semibold">Source Website</label>
              <input value={form.source_website} onChange={e => setField('source_website', e.target.value)}
                placeholder="farfetch.com"
                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm font-work mt-1.5 focus:outline-none focus:border-[#D4AF37]" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#999999] font-work font-semibold">Source URL</label>
              <input value={form.source_url} onChange={e => setField('source_url', e.target.value)}
                placeholder="https://..."
                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm font-work mt-1.5 focus:outline-none focus:border-[#D4AF37]" />
            </div>
            <div className="col-span-2">
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#999999] font-work font-semibold">Colour Group (optional)</label>
              <input value={form.variant_group} onChange={e => setField('variant_group', e.target.value)}
                placeholder="e.g. prada-americas-cup — same key links colourways as swatches"
                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm font-work mt-1.5 focus:outline-none focus:border-[#D4AF37]"
                data-testid="product-variant-group-input" />
              <p className="text-[10px] text-[#AAAAAA] font-work mt-1">Leave empty to auto-link items with the same designer and name.</p>
            </div>
          </div>

          {/* Active toggle (edit only) */}
          {isEditing && (
            <div className="flex items-center gap-3 pt-3 border-t border-[#F3F3F3]">
              <button type="button" onClick={() => setField('is_active', !form.is_active)}
                className={`relative w-10 h-5 rounded-full transition-colors flex items-center flex-shrink-0 ${form.is_active ? 'bg-[#2E7D32]' : 'bg-[#DDDDDD]'}`}
                data-testid="is-active-toggle">
                <span className={`absolute w-3.5 h-3.5 rounded-full bg-white shadow transition-transform ${form.is_active ? 'translate-x-5' : 'translate-x-1'}`} />
              </button>
              <span className="text-xs font-work text-[#555555]">
                {form.is_active ? 'Active — visible on store' : 'Inactive — hidden from store'}
              </span>
            </div>
          )}

          {saveMsg && <p className="text-xs text-red-500 font-work" data-testid="save-error">{saveMsg}</p>}

          <button onClick={handleSave} disabled={saveLoading}
            className="w-full bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors py-4 text-xs uppercase tracking-widest font-semibold font-work disabled:opacity-50"
            data-testid="save-product-btn">
            {saveLoading ? 'Saving...' : isEditing ? 'Update Product' : 'Add to Catalog'}
          </button>
        </div>
      </div>
    </div>
  );
}
